<template>
    <div class="answers" v-if="answers && answers.length">
        <div
            class="answer"
            v-for="answer in answers"
            :key="`${answer.answers_id || 'a'}.${answer.forms_steps_questions_id || answer.id}`"
        >
            <span class="question-icon" :class="toneClass(answer)">
                <span :class="iconFor(answer)"></span>
            </span>
            <div class="question-info">
                <div class="question">{{ answer.question }}</div>
                <div class="answerValue" :class="toneClass(answer)">
                    <template v-if="isUnanswered(answer)">
                        {{ t("inbox-message-unanswered") }}
                    </template>
                    <template v-else-if="answer.type === 'boolean'">
                        {{
                            parseInt(answer.valueNumber) > 0
                                ? t("inbox-message-bool_yes")
                                : t("inbox-message-bool_no")
                        }}
                    </template>
                    <template v-else-if="answer.type === 'emotion'">
                        <filled-circles
                            :filled="parseFloat(answer.valueNumber)"
                        />
                    </template>
                    <template
                        v-else-if="
                            answer.type === 'comment' || answer.type === 'input'
                        "
                    >
                        <p v-html="formatText(answer.valueText)"></p>
                    </template>
                    <template v-else-if="answer.type === 'selectbox'">
                        {{ formatSelect(answer) }}
                    </template>
                    <template v-else-if="answer.type === 'nps'">
                        <span class="score-value">
                            {{ parseInt(answer.valueNumber) }}
                        </span>
                        <span class="score-band">
                            {{ t(npsBand(answer.valueNumber)) }}
                        </span>
                    </template>
                    <template v-else-if="answer.type === 'ces'">
                        <span class="score-value">
                            {{ parseInt(answer.valueNumber) }}
                        </span>
                        <span class="score-band">{{ answer.valueText }}</span>
                    </template>
                    <template v-else-if="answer.type === 'date'">
                        {{ answer.valueText }}
                    </template>
                    <template v-else-if="answer.type === 'file'">
                        <ul class="answer-files" v-if="safeFiles(answer).length">
                            <li
                                v-for="(file, i) in safeFiles(answer)"
                                :key="i"
                                :class="{ 'has-preview': showsPreview(file) }"
                            >
                                <a
                                    v-if="showsPreview(file)"
                                    class="file-thumb"
                                    :href="file.url"
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    :title="file.name"
                                >
                                    <img
                                        :src="file.url"
                                        :alt="file.name"
                                        loading="lazy"
                                        @error="previewFailed(file)"
                                    />
                                </a>
                                <a
                                    class="file-name"
                                    :href="file.url"
                                    target="_blank"
                                    rel="noopener noreferrer"
                                >
                                    <span class="mdi mdi-paperclip"></span>
                                    {{ file.name }}
                                </a>
                            </li>
                        </ul>
                        <span v-else class="no-files">
                            {{ t("inbox-message-no_files") }}
                        </span>
                    </template>
                    <template v-else>
                        {{ answer.valueText }}
                    </template>
                </div>
            </div>
        </div>
    </div>
    <div v-else class="answers-empty">
        {{ emptyLabel }}
    </div>
</template>

<script>
import FilledCircles from "../filled-circles/filled-circles";
import {
    answerToneClass,
    fileNameFromUrl,
    formatAnswerText,
    isPreviewableImage,
    formatSelectboxValue,
    getIconForAnswer,
    npsBandKey,
    safeFileUrl,
} from "../../helpers/answerDisplay";

export default {
    name: "AnswerList",
    components: { FilledCircles },
    props: {
        answers: {
            type: Array,
            default: () => [],
        },
        emptyText: {
            type: String,
            default: "",
        },
    },
    data() {
        return {
            // Urls whose thumbnail failed to load (e.g. expired link): they
            // fall back to the plain file link.
            failedPreviews: {},
        };
    },
    computed: {
        emptyLabel() {
            return this.emptyText || this.t("tasks-linked_answer_none");
        },
    },
    methods: {
        t(term, replaces) {
            return this.$root.t(term, replaces);
        },
        toneClass: answerToneClass,
        iconFor: getIconForAnswer,
        formatText: formatAnswerText,
        formatSelect: formatSelectboxValue,
        npsBand: npsBandKey,
        isUnanswered(answer) {
            if (!answer || answer.type === "file") {
                return false;
            }
            if (
                ["boolean", "emotion", "nps", "ces"].includes(answer.type)
            ) {
                return (
                    answer.valueNumber == null || answer.valueNumber === ""
                );
            }
            return !String(answer.valueText || "").trim();
        },
        // File urls come from the respondent, so anything that is not http(s)
        // never reaches an href.
        showsPreview(file) {
            return isPreviewableImage(file) && !this.failedPreviews[file.url];
        },
        previewFailed(file) {
            this.$set(this.failedPreviews, file.url, true);
        },
        safeFiles(answer) {
            return (answer.files || [])
                .map((file) => ({
                    url: safeFileUrl(file.url),
                    name: file.name || fileNameFromUrl(file.url),
                }))
                .filter((file) => file.url);
        },
    },
};
</script>

<style scoped lang="less">
.answers {
    display: flex;
    flex-direction: column;
    gap: 0;
}

.answers-empty {
    color: #9da4ae;
    font-size: 13px;
    font-style: italic;
}

.answer {
    display: flex;
    align-items: flex-start;
    gap: 12px;
    padding: 14px 0;
    border-bottom: 1px solid #f3f4f6;

    &:first-child {
        padding-top: 0;
    }

    &:last-child {
        border-bottom: none;
        padding-bottom: 0;
    }

    .question-icon {
        flex-shrink: 0;
        width: 28px;
        height: 28px;
        border-radius: 8px;
        background: #f3f4f6;
        display: inline-flex;
        align-items: center;
        justify-content: center;
        color: #6c737f;

        .answer-type-icon {
            font-size: 16px;
            line-height: 1;
            color: currentColor;
        }

        &.tone-good {
            background: #edfcf3;
            color: #0b9055;
        }

        &.tone-neutral {
            background: #fff7d4;
            color: #f79009;
        }

        &.tone-bad {
            background: #fee4e2;
            color: #f04438;
        }

        &.tone-muted {
            background: #f3f4f6;
            color: #6c737f;
        }
    }

    .question-info {
        flex: 1;
        min-width: 0;
    }

    .question {
        display: block;
        margin-bottom: 4px;
        font-size: 12px;
        font-weight: 500;
        color: #6c737f;
    }

    .answerValue {
        color: #384250;
        font-size: 14px;
        font-weight: 400;
        line-height: 1.45;

        p {
            margin: 0;
        }

        .score-value {
            font-weight: 500;
            font-variant-numeric: tabular-nums;
            margin-right: 6px;
        }

        .score-band {
            color: #6c737f;
            font-size: 13px;
        }

        &.tone-good .score-value {
            color: #0b9055;
        }

        &.tone-neutral .score-value {
            color: #f79009;
        }

        &.tone-bad .score-value {
            color: #f04438;
        }

        .answer-files {
            list-style: none;
            margin: 0;
            padding: 0;

            li {
                margin-bottom: 4px;

                &.has-preview {
                    display: flex;
                    flex-direction: column;
                    align-items: flex-start;
                    gap: 4px;
                    margin-bottom: 10px;
                }
            }

            a {
                color: var(--primary);
                word-break: break-all;

                .mdi {
                    margin-right: 4px;
                }
            }

            .file-thumb {
                display: block;
                width: 160px;
                height: 160px;
                border: 1px solid #e5e7eb;
                border-radius: 6px;
                overflow: hidden;
                background: #f4f5f7;

                img {
                    display: block;
                    width: 100%;
                    height: 100%;
                    object-fit: contain;
                }
            }
        }

        .no-files {
            color: #9da4ae;
            font-style: italic;
        }
    }
}
</style>
